package fr.frux.feature.map

import fr.frux.shared.domain.model.Tree

/**
 * Bandeau d'info affiche au-dessus de la carte, sous la barre de recherche (#163). Un seul a la
 * fois : avant ce helper, jusqu'a trois bandeaux (lot tronque, cache perime, zone vide) pouvaient
 * s'empiler en meme temps que la pastille de re-requete, poussant les controles tres bas sur un
 * ecran etroit.
 *
 * La pastille « Rechercher dans cette zone » n'entre pas dans ce type : c'est une action, pas une
 * info, et elle s'affiche en plus de ce bandeau, toujours en dernier (cf. [MapScreen]).
 */
sealed interface MapNotice {
    /** Zone interrogee vide sous un filtre serveur actif : priorite maximale, l'utilisateur peut agir. */
    data class FilteredEmpty(
        val emptiness: FilteredNoResults,
    ) : MapNotice

    /** Zone interrogee sans aucun arbre, hors de tout filtre (#478, #502) : carte compacte [EmptyAreaCard]. */
    data class EmptyArea(
        val emptiness: FilteredNoResults,
    ) : MapNotice

    /** Pastille unique de première session en zone peuplée : dit où ajouter un arbre manquant. */
    data object PopulatedHint : MapNotice

    /** Le plafond client a tronque le lot avant que le total serveur ne soit epuise (#155). */
    data class TruncatedBatch(
        val shown: Int,
        val total: Int,
    ) : MapNotice

    /** Lot affiche perime, rechargement reseau en echec (#62). */
    data object StaleCache : MapNotice
}

/**
 * Choisit LE bandeau a montrer parmi ceux que l'etat de l'ecran rend possibles, par ordre de
 * priorite decroissante : zone vide filtree (l'utilisateur peut agir dessus) > zone vide sans
 * filtre (#478, rien d'autre a dire d'un lot vide) > pastille de zone peuplee (une seule fois,
 * et les zones denses sont celles ou le lot est tronque) > lot tronque (le
 * constat le plus actionnable des deux restants, un zoom peut le resoudre) > cache perime (le
 * moins urgent, les donnees restent utilisables).
 *
 * Fonction pure, hors du ViewModel (sous ratchet detekt `LargeClass`) et hors Compose : testable
 * sans harnais d'UI, sur tous les cas de cumul.
 */
fun selectMapNotice(
    filteredNoResults: FilteredNoResults?,
    truncatedTotal: Int?,
    shownCount: Int,
    showingStaleCache: Boolean,
    emptyArea: FilteredNoResults? = null,
    populatedHint: Boolean = false,
): MapNotice? =
    when {
        filteredNoResults != null -> MapNotice.FilteredEmpty(filteredNoResults)
        emptyArea != null -> MapNotice.EmptyArea(emptyArea)
        populatedHint -> MapNotice.PopulatedHint
        truncatedTotal != null -> MapNotice.TruncatedBatch(shown = shownCount, total = truncatedTotal)
        showingStaleCache -> MapNotice.StaleCache
        else -> null
    }

/** Les arbres actuellement a l'ecran : une selection d'espece prevaut sur le texte de recherche. */
internal fun MapState.displayedTrees(): List<Tree> =
    if (selectedSpeciesRollupId != null) {
        visibleTrees
    } else if (searchQuery.length >= MIN_SEARCH_QUERY_LENGTH) {
        searchResults
    } else {
        visibleTrees
    }
